
import React, { useState, useEffect } from 'react';
import AnimateInView from '@/components/ui/animate-in-view';
import { Separator } from '@/components/ui/separator';

interface ClientReview {
  name: string;
  company: string;
  stars: number;
  text: string;
}

interface ClientReviewsCarouselProps {
  reviews: ClientReview[];
}

const ClientReviewsCarousel: React.FC<ClientReviewsCarouselProps> = ({ reviews }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  
  // Auto-rotate reviews every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((current) => (current + 1) % reviews.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [reviews.length]);
  
  return (
    <section className="py-16 bg-white" aria-labelledby="client-reviews-heading">
      <div className="container mx-auto px-4">
        <div className="flex flex-col items-center mb-10">
          <div className="h-0.5 bg-gold w-20 mb-4"></div>
          <h2 id="client-reviews-heading" className="text-3xl md:text-4xl font-serif text-forti-fi text-center mb-6">
            Client Experiences
          </h2>
          <p className="text-lg text-center text-forti-fi-charcoal max-w-2xl">
            Hear directly from clients who have implemented our bespoke security solutions for their unique properties.
          </p>
        </div>
        
        <div className="max-w-4xl mx-auto">
          <div className="relative bg-forti-fi-background p-8 md:p-10 rounded-lg shadow-md">
            <AnimateInView key={activeIndex} animation="fade-in">
              <div className="text-5xl text-forti-fi-teal mb-4">"</div>
              <p className="text-xl italic text-forti-fi-charcoal mb-8">
                {reviews[activeIndex].text}
              </p>
              <div className="flex flex-col md:flex-row md:items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium">{reviews[activeIndex].name}</h3>
                  <p className="text-forti-fi-charcoal">{reviews[activeIndex].company}</p>
                </div>
                <div className="flex mt-3 md:mt-0">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className={i < reviews[activeIndex].stars ? "text-amber-500" : "text-gray-300"}>
                      ★
                    </span>
                  ))}
                </div>
              </div>
            </AnimateInView>
            
            <div className="flex justify-center mt-8">
              {reviews.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveIndex(index)}
                  className={`w-3 h-3 mx-1 rounded-full transition-colors ${
                    index === activeIndex ? 'bg-forti-fi' : 'bg-gray-300'
                  }`}
                  aria-label={`Go to review ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ClientReviewsCarousel;
